import { useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Skeleton } from '../ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { CloudOff, Download, Loader2, RotateCw, Trash2 } from 'lucide-react';
import type { CloudSaveListItem } from '../../hooks/useCloudSaves';

interface EmulatorSaveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  saves: CloudSaveListItem[];
  isLoading: boolean;
  loadingSaveId?: string | null;
  deletingSaveId?: string | null;
  onLoad: (save: CloudSaveListItem) => void | Promise<void>;
  onDelete: (save: CloudSaveListItem) => void | Promise<void>;
  onRetryPending?: () => void | Promise<void>;
}

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const formatTimestamp = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));

export const EmulatorSaveDialog = ({
  open,
  onOpenChange,
  saves,
  isLoading,
  loadingSaveId,
  deletingSaveId,
  onLoad,
  onDelete,
  onRetryPending,
}: EmulatorSaveDialogProps) => {
  const [saveToDelete, setSaveToDelete] = useState<CloudSaveListItem | null>(null);
  const pendingCount = saves.filter((save) => save.status === 'pending').length;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl border-border/70 bg-background/95 p-0 backdrop-blur-sm">
          <DialogHeader className="border-b border-border/70 px-6 py-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <DialogTitle>Load a save state</DialogTitle>
                <DialogDescription className="mt-1">
                  Latest 10 save states for this game. Autosaves are kept private and pruned automatically.
                </DialogDescription>
              </div>
              {pendingCount > 0 && onRetryPending ? (
                <Button variant="outline" size="sm" onClick={() => void onRetryPending()} className="gap-2">
                  <RotateCw className="h-4 w-4" />
                  Sync {pendingCount} pending
                </Button>
              ) : null}
            </div>
          </DialogHeader>

          <ScrollArea className="max-h-[70vh]">
            <div className="px-6 py-5">
              {isLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="grid grid-cols-[140px_1.3fr_0.7fr_0.7fr_1.4fr_180px] gap-3">
                      <Skeleton className="h-20 w-full rounded-xl" />
                      <Skeleton className="h-20 w-full rounded-xl" />
                      <Skeleton className="h-20 w-full rounded-xl" />
                      <Skeleton className="h-20 w-full rounded-xl" />
                      <Skeleton className="h-20 w-full rounded-xl" />
                      <Skeleton className="h-20 w-full rounded-xl" />
                    </div>
                  ))}
                </div>
              ) : saves.length === 0 ? (
                <div className="flex min-h-[220px] flex-col items-center justify-center rounded-2xl border border-dashed border-border/70 bg-muted/20 p-8 text-center">
                  <Download className="mb-3 h-8 w-8 text-muted-foreground" />
                  <h3 className="text-base font-semibold">No save states yet</h3>
                  <p className="mt-2 max-w-md text-sm text-muted-foreground">
                    Save your game manually or stop a session with autosave enabled to create your first cloud state.
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[150px]">Preview</TableHead>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {saves.map((save) => {
                      const isLoadingRow = loadingSaveId === save.id;
                      const isDeletingRow = deletingSaveId === save.id;

                      return (
                        <TableRow key={save.id}>
                          <TableCell>
                            <div className="overflow-hidden rounded-xl border border-border/70 bg-black/40">
                              {save.screenshot_url ? (
                                <img
                                  src={save.screenshot_url}
                                  alt="Save preview"
                                  className="h-20 w-full object-cover"
                                />
                              ) : (
                                <div className="flex h-20 items-center justify-center text-xs text-muted-foreground">
                                  No preview
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="font-medium">{formatTimestamp(save.created_at)}</div>
                              {save.status === 'pending' ? (
                                <div className="inline-flex items-center gap-1 text-xs text-amber-600">
                                  <CloudOff className="h-3.5 w-3.5" />
                                  Pending sync
                                </div>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-2">
                              <Badge variant={save.type === 'manual' ? 'default' : 'secondary'}>
                                {save.type === 'manual' ? 'Manual' : 'Autosave'}
                              </Badge>
                              {save.status === 'pending' ? <Badge variant="outline">Pending</Badge> : null}
                            </div>
                          </TableCell>
                          <TableCell>{formatBytes(save.size_bytes)}</TableCell>
                          <TableCell className="max-w-[280px] text-sm text-muted-foreground">
                            {save.notes?.trim() ? save.notes : '—'}
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                onClick={() => void onLoad(save)}
                                disabled={isLoadingRow || isDeletingRow}
                                className="gap-2"
                              >
                                {isLoadingRow ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                                Load
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setSaveToDelete(save)}
                                disabled={isLoadingRow || isDeletingRow}
                                className="gap-2"
                              >
                                {isDeletingRow ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                Delete
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(saveToDelete)} onOpenChange={(open) => !open && setSaveToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this save state?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the save metadata and its private storage assets. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!saveToDelete) {
                  return;
                }

                void onDelete(saveToDelete);
                setSaveToDelete(null);
              }}
            >
              Delete save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
